/**
 * Personal Extensions Contracts
 *
 * Contracts for the personal/account-level Extensions DataTable.
 * These are for users viewing their own installed extensions (without organization context).
 *
 * For org-level extension contracts, see ./extensions.ts
 */
import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { ColumnDataTypes, DATE_RANGE_OPERATORS, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { extensionListItemSchema, extensionSourceSchema, validateScanStatusFilterValues, } from './extensions';
import { severitySchema } from './shared/severity';
// Re-export shared types for convenience
export { extensionListItemSchema };
/**
 * Sort field options for personal extensions DataTable.
 * Note: userCount is intentionally excluded since personal extensions
 * are always scoped to a single user (count is always 1).
 */
export const personalExtensionSortFieldSchema = z.enum([
    'name',
    'publisher',
    'firstSeenAt',
    'lastSeenAt',
    'source',
    'ide',
    'security',
    'riskEstimate',
    'scanStatus',
]);
/**
 * Valid values for the source column filter.
 * "built-in" = extension ID matches IDE's built-in prefix; "added" = user-installed.
 */
export const extensionSourceFilterValueSchema = z.enum(['built-in', 'added']);
/**
 * Valid values for the IDE column filter.
 * Each value corresponds to an extension source platform.
 */
export const extensionIdeFilterValueSchema = extensionSourceSchema;
// ============================================================================
// Personal Extensions DataTable-specific schemas
// ============================================================================
/**
 * Filterable fields for the personal extensions DataTable.
 * Personal-level excludes USER field (no users table join in personal context).
 *
 * When org-level DataTable is added, it will include USER field for filtering
 * by specific users within the organization.
 */
export const PERSONAL_EXTENSION_FILTERABLE_FIELDS = {
    NAME: 'name',
    SOURCE: 'source',
    PUBLISHER: 'publisher',
    IDE: 'ide',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    SECURITY: 'security',
    RISK_ESTIMATE: 'riskEstimate',
    SCAN_STATUS: 'scanStatus',
};
/**
 * Searchable fields for the personal extensions DataTable.
 * These are the visible columns that full-text search queries against.
 *
 * Personal extensions visible columns: Name, Publisher, IDE, First Seen
 * - Name searches: displayName
 * - Publisher searches: publisher
 * - IDE and First Seen are not text-searchable (array and date types)
 */
export const PERSONAL_EXTENSION_SEARCHABLE_FIELDS = {
    DISPLAY_NAME: 'displayName',
    PUBLISHER: 'publisher',
    IDE: 'ide',
};
/**
 * Sort fields for the personal extensions DataTable.
 * Mirrors personalExtensionSortFieldSchema but as a const object for runtime use.
 */
export const PERSONAL_EXTENSION_SORT_FIELDS = {
    NAME: 'name',
    PUBLISHER: 'publisher',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    SOURCE: 'source',
    IDE: 'ide',
    SECURITY: 'security',
    RISK_ESTIMATE: 'riskEstimate',
    SCAN_STATUS: 'scanStatus',
};
/** Narrows firstSeenAt to range operators plus the value-less operators (no `is`/`is not`); shared by both schemas. */
export const PERSONAL_EXTENSION_OPERATOR_OVERRIDES = {
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT]: DATE_RANGE_OPERATORS,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT]: DATE_RANGE_OPERATORS,
};
/**
 * Shared refinement function to validate personal extension filter values.
 * Enforces per-column operator overrides and validates source/IDE filter values.
 *
 * Use with `.superRefine()` on any schema that includes personal extension filters.
 *
 * @param filters - Array of filter values (can be undefined)
 * @param ctx - Zod refinement context for adding issues
 */
export function validatePersonalExtensionFilters(filters, ctx) {
    const overrides = PERSONAL_EXTENSION_OPERATOR_OVERRIDES;
    filters?.forEach((filter, index) => {
        const override = overrides[filter.field];
        if (override && !override.includes(filter.operator)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['filters', index, 'operator'],
                message: `Operator "${filter.operator}" is not allowed for field "${filter.field}". Allowed operators: ${override.join(', ')}.`,
            });
        }
    });
    const sourceFilter = filters?.find(f => f.field === PERSONAL_EXTENSION_FILTERABLE_FIELDS.SOURCE);
    if (sourceFilter) {
        for (const val of sourceFilter.value) {
            if (!extensionSourceFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid source filter value: "${val}". Must be one of: built-in, added`,
                });
            }
        }
    }
    const ideFilter = filters?.find(f => f.field === PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE);
    if (ideFilter) {
        for (const val of ideFilter.value) {
            if (!extensionIdeFilterValueSchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid IDE filter value: "${val}". Must be one of: ${extensionSourceSchema.options.join(', ')}`,
                });
            }
        }
    }
    const securityFilter = filters?.find(f => f.field === PERSONAL_EXTENSION_FILTERABLE_FIELDS.SECURITY);
    if (securityFilter) {
        for (const val of securityFilter.value) {
            if (!severitySchema.safeParse(val).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['filters'],
                    message: `Invalid security filter value: "${val}". Must be one of: ${severitySchema.options.join(', ')}`,
                });
            }
        }
    }
    validateScanStatusFilterValues(filters?.find(f => f.field === PERSONAL_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS), ctx);
}
/**
 * Column data types for personal extension DataTable fields.
 * Used by createTableQuerySchema to validate operator × datatype combinations.
 */
export const PERSONAL_EXTENSION_COLUMN_DATA_TYPES = {
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.SOURCE]: ColumnDataTypes.ENUM,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER]: ColumnDataTypes.STRING,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE]: ColumnDataTypes.ENUM,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.SECURITY]: ColumnDataTypes.ENUM,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE]: ColumnDataTypes.ENUM,
    [PERSONAL_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS]: ColumnDataTypes.ENUM,
};
/**
 * Query parameters for the personal extensions DataTable API call (frontend → backend).
 * Accepts a pre-parsed filters array — not bracket notation.
 */
export const listPersonalExtensionsDataTableParamsSchema = z
    .object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    filters: FiltersSchema.optional(),
    sortBy: personalExtensionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    knownTotal: knownTotalParamsSchema,
})
    .superRefine((data, ctx) => validatePersonalExtensionFilters(data.filters, ctx));
/**
 * Backend query schema for personal extensions DataTable.
 * Handles bracket-notation filter params from Fastify OR a pre-parsed filters array.
 */
const personalExtensionDataTableQueryBaseSchema = paginationSchema.extend({
    sortBy: personalExtensionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
export const personalExtensionDataTableQuerySchema = createTableQuerySchema(personalExtensionDataTableQueryBaseSchema, PERSONAL_EXTENSION_COLUMN_DATA_TYPES, PERSONAL_EXTENSION_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    validatePersonalExtensionFilters(data.filters, ctx);
});
/**
 * Response schema for the personal extensions DataTable endpoint.
 * Shape: { data: ExtensionListItem[], pagination: {...}, meta: {...} }
 */
export const personalExtensionTableResponseSchema = z.object({
    data: z.array(extensionListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * Fields with server-backed dynamic filter options.
 * name/publisher return distinct display strings; ide returns the raw
 * platform ids actually present in the user's inventory so the dropdown
 * mirrors the table (KIR-2730). source uses static enum options and
 * firstSeenAt is a date picker.
 */
export const personalExtensionFilterOptionFieldSchema = z.enum([
    PERSONAL_EXTENSION_FILTERABLE_FIELDS.NAME,
    PERSONAL_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
    PERSONAL_EXTENSION_FILTERABLE_FIELDS.IDE,
    PERSONAL_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
    PERSONAL_EXTENSION_FILTERABLE_FIELDS.SCAN_STATUS,
]);
/**
 * Query schema for fetching personal extension filter options.
 */
export const personalExtensionFilterOptionsQuerySchema = z.object({
    field: personalExtensionFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Response schema for personal extension filter options.
 */
export const personalExtensionFilterOptionsResponseSchema = z.object({
    field: personalExtensionFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(1),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
//# sourceMappingURL=account-extensions.js.map